// src/admin/AdminProducts.jsx
import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { colors } from "../theme";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Avatar,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddProductForm from "./AddProductForm";
import { API_BASE, SHEETS } from "../utils/api";

const typeMap = {
  All: null,
  Mix: "Mix",
  Angle: "A",
  Flat: "F",
  Round: "R",
  Square: "S",
  Channel: "C",
  Rejected: "Rejected",
};

const typeFullName = {
  A: "Angle",
  F: "Flat",
  R: "Round",
  S: "Square",
  C: "Channel",
  Mix: "Mix",
  Rejected: "Rejected",
};

export default function AdminProducts() {
  const { searchQuery = "", selectedCategory = "All" } = useOutletContext();

  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null); // Full product object for editing
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detail Popup
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Delete Confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingProductName, setDeletingProductName] = useState("");

  // Fetch all stocks from Google Sheets
  const fetchStocks = async () => {
    try {
      setLoading(true);
      const res = await fetch(SHEETS.products);

      if (!res.ok) throw new Error("Failed to load stocks");
      const data = await res.json();

      // Normalize data into array
      const dataArray = Array.isArray(data) ? data : [data];

      // Map Google fields to match your component's state
      const mappedData = dataArray.map((item, index) => ({
        _id: index + 1, // temporary ID, replace if Google gives an ID
        standardName: item["Standard Name"] || "—",
        size: item["SIZE"] || "—",
        type: item["TYPE"] || "—",
        botStock: item["Today Stock"] || 0,
        rate: item["RATE"] || 0,
        company: item["Company"] || "—",
        length: item["LENGTH"] || "—",
        parity: item["Basic/Patti"] || "—",
        img: item["Image"] || "/images/default-steel.jpg",
        createdAt: new Date().toISOString(), // fallback date
      }));

      setStocks(mappedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStocks();
  }, []);

  // Filter products
  const filteredProducts = stocks.filter((item) => {
    let matches = true;

    if (selectedCategory !== "All") {
      const expectedType = typeMap[selectedCategory];
      if (expectedType) {
        matches =
          expectedType === "Mix" || expectedType === "Rejected"
            ? item.type === expectedType
            : item.type?.toLowerCase() === expectedType?.toLowerCase();
      } else {
        matches = false;
      }
    }

    if (matches && searchQuery.trim()) {
      const kw = searchQuery.toLowerCase();
      matches =
        item.standardName?.toLowerCase().includes(kw) ||
        item.size?.toLowerCase().includes(kw) ||
        item.type?.toLowerCase().includes(kw);
    }

    return matches;
  });

  // Edit Product
  const handleEdit = (product) => {
    setEditProduct(product);
    setShowForm(true);
  };

  // Close Form
  const closeForm = () => {
    setShowForm(false);
    setEditProduct(null);
  };

  // Delete Handlers
  const handleDeleteClick = (id, name) => {
    setDeletingId(id);
    setDeletingProductName(name);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!deletingId) return;

    try {
      const res = await fetch(`${API_BASE}/stocks/${deletingId}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to delete");
      }

      setStocks((prev) => prev.filter((item) => item._id !== deletingId));
      setDeleteDialogOpen(false);
      setDeletingId(null);
      setDeletingProductName("");
    } catch (err) {
      alert(err.message || "Delete failed");
      setDeleteDialogOpen(false);
    }
  };

  // Open Detail Popup
  const handleRowClick = (params) => {
    setSelectedProduct(params.row);
    setDetailOpen(true);
  };

  const columns = [
    {
      field: "img",
      headerName: "Image",
      width: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => (
        <Avatar
          src={params.value || "/images/default-steel.jpg"}
          variant="rounded"
          sx={{ width: 56, height: 56 }}
        />
      ),
    },
    {
      field: "standardName",
      headerName: "Product Name",
      flex: 1,
      minWidth: 320,

      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography fontWeight={600} noWrap>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "size",
      headerName: "Size",
      width: 130,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "type",
      headerName: "Type",
      width: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography fontWeight="bold">
          {typeFullName[params.value] || params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "botStock",
      headerName: "Stock",
      width: 160,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography
          fontWeight="bold"
          color={params.value < 1 ? "error.main" : "success.main"}
        >
          {Number(params.value).toLocaleString() || 0} Kgs
        </Typography>
      ),
    },
    {
      field: "rate",
      headerName: "Rate",
      width: 140,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography color="success.main" fontWeight="bold">
          ₹{Number(params.value).toFixed(2)}/kg
        </Typography>
      ),
    },
    {
      field: "company",
      headerName: "Company",
      width: 120,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value || "N/A"}
          size="small"
          sx={{
            bgcolor: "#e8f5e8",
            color: "#2e7d32",
            fontWeight: "bold",
            minWidth: 80,
          }}
        />
      ),
    },
    // {
    //   field: "actions",
    //   headerName: "Actions",
    //   width: 120,
    //   align: "center",
    //   headerAlign: "center",
    //   sortable: false,
    //   renderCell: (params) => (
    //     <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
    //       <IconButton
    //         size="small"
    //         color="primary"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           handleEdit(params.row);
    //         }}
    //       >
    //         <EditIcon fontSize="small" />
    //       </IconButton>
    //       <IconButton
    //         size="small"
    //         color="error"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           handleDeleteClick(params.row._id, params.row.standardName);
    //         }}
    //       >
    //         <DeleteIcon fontSize="small" />
    //       </IconButton>
    //     </Box>
    //   ),
    // },
  ];

  return (
    <Box sx={{ bgcolor: colors.lightGrey, minHeight: "100vh", pt: "90px" }}>
      <Box sx={{ maxWidth: "1600px", mx: "auto", px: { xs: 3, md: 6 }, py: 4 }}>
        {/* Header */}
        {!showForm && (
          <Box
            mb={4}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <div>
              <Typography variant="h4" fontWeight="bold" fontFamily="Arvo">
                {selectedCategory === "All" ? "All Products" : selectedCategory}
              </Typography>
              <Typography color="text.secondary">
                Showing {filteredProducts.length} items
              </Typography>
            </div>

            {/* <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditProduct(null);
                setShowForm(true);
              }}
              sx={{
                bgcolor: "#000",
                color: "#fff",
                fontWeight: "bold",
                px: 4,
                py: 1.5,
                borderRadius: 3,
                boxShadow: 6,
                "&:hover": { bgcolor: "#333" },
              }}
            >
              Add Product
            </Button> */}
          </Box>
        )}

        {/* Add or Edit Form */}
        {showForm && (
          <AddProductForm
            closeForm={closeForm}
            onSuccess={() => {
              fetchStocks();
              closeForm();
            }}
            productToEdit={editProduct}
          />
        )}

        {/* Loading / Error / Table */}
        {!showForm && loading && (
          <Box
            sx={{
              height: 600,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}

        {!showForm && error && (
          <Alert severity="error" sx={{ borderRadius: 3, mb: 4 }}>
            {error} — Is your backend running on {API_BASE}?
          </Alert>
        )}

        {!showForm && !loading && !error && (
          <Box
            sx={{
              height: 600,
              width: "100%",
              bgcolor: "white",
              borderRadius: 4,
              overflow: "hidden",
              boxShadow: 12,
            }}
          >
            <DataGrid
              rows={filteredProducts}
              columns={columns}
              getRowId={(row) => row._id}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              disableSelectionOnClick
              onRowClick={handleRowClick}
              sx={{
                border: 0,
                cursor: "pointer",
                "& .MuiDataGrid-columnHeaders": {
                  bgcolor: "#fff8e1",
                  fontWeight: "bold",
                  fontSize: "15px",
                  color: "#b45309",
                },
                "& .MuiDataGrid-row:hover": {
                  bgcolor: "#fffde7",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
              }}
              components={{
                NoRowsOverlay: () => (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      No products found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Try changing the category or search query
                    </Typography>
                  </Box>
                ),
              }}
            />
          </Box>
        )}

        {/* Product Detail Dialog */}
        <Dialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Product Details
            <IconButton
              onClick={() => setDetailOpen(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          {selectedProduct && (
            <>
              <DialogContent dividers sx={{ py: 5 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    gap: 5,
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ flex: "0 0 380px", textAlign: "center" }}>
                    <img
                      src={selectedProduct.img || "/images/default-steel.jpg"}
                      alt={selectedProduct.standardName}
                      style={{
                        width: "100%",
                        maxWidth: 380,
                        height: 320,
                        objectFit: "cover",
                        borderRadius: 24,
                        boxShadow: "0 15px 35px rgba(0,0,0,0.2)",
                        border: "4px solid white",
                      }}
                    />
                    <Typography variant="h5" fontWeight="bold" sx={{ mt: 3 }}>
                      {selectedProduct.standardName}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      {selectedProduct.size} •{" "}
                      {typeFullName[selectedProduct.type] ||
                        selectedProduct.type}
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 3.5,
                      }}
                    >
                      <DetailItem
                        label="Rate (per kg)"
                        value={`₹${Number(selectedProduct.rate).toFixed(2)}`}
                        color="success.main"
                      />
                      <DetailItem
                        label="Available Stock"
                        value={`${Number(
                          selectedProduct.botStock
                        ).toLocaleString()} Kgs`}
                        color={
                          selectedProduct.botStock < 1
                            ? "error.main"
                            : "success.main"
                        }
                      />
                      <DetailItem
                        label="Length"
                        value={selectedProduct.length}
                      />
                      <DetailItem
                        label="Parity / Extra"
                        value={selectedProduct.parity}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight="medium"
                        >
                          Company
                        </Typography>
                        <Chip
                          label={selectedProduct.company}
                          size="large"
                          sx={{
                            mt: 1,
                            fontWeight: "bold",
                            bgcolor: "#d1fae5",
                            color: "#065f46",
                          }}
                        />
                      </Box>
                      <DetailItem
                        label="Added On"
                        value={new Date(
                          selectedProduct.createdAt
                        ).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                        })}
                      />
                    </Box>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button
                  onClick={() => {
                    setDetailOpen(false);
                    handleEdit(selectedProduct);
                  }}
                  variant="contained"
                  startIcon={<EditIcon />}
                  size="large"
                >
                  Edit Product
                </Button>
                <Button onClick={() => setDetailOpen(false)} size="large">
                  Close
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ pb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "#fee2e2",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DeleteIcon sx={{ fontSize: 32, color: "#dc2626" }} />
              </Box>
              <Typography variant="h6" fontWeight="bold">
                Delete Product?
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" color="text.secondary">
              Are you sure you want to permanently delete this product?
            </Typography>
            <Typography
              variant="body2"
              sx={{ mt: 2, fontWeight: "bold", color: "#dc2626" }}
            >
              {deletingProductName || "This product"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action <strong>cannot be undone</strong>.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingId(null);
                setDeletingProductName("");
              }}
              variant="outlined"
              size="large"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              variant="contained"
              color="error"
              size="large"
              sx={{
                minWidth: 140,
                bgcolor: "#dc2626",
                "&:hover": { bgcolor: "#b91c1c" },
              }}
            >
              Yes, Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

// Helper Component
const DetailItem = ({ label, value, color = "text.primary" }) => (
  <Box>
    <Typography variant="body2" color="text.secondary" fontWeight="medium">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight="bold" color={color} sx={{ mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);
